import { useEffect, useState } from "react";

export const LOCAL_BOOKINGS_STORAGE_KEY = "mj-local-bookings";
export const LOCAL_BOOKINGS_CHANGED_EVENT = "mj-local-bookings-changed";
const MAX_LOCAL_BOOKINGS = 30;

export type LocalBookingStatus = "pending" | "confirmed" | "completed" | "cancelled";

export interface LocalBooking {
  id: string;
  reference: string;
  slug: string;
  title: string;
  image?: string;
  city: string;
  date: string;
  time?: string;
  /** End of a flexible package's window: the card reads "13:00–15:00". */
  timeEnd?: string | null;
  guests: number;
  pkg?: string;
  total: number;
  currency: string;
  status: LocalBookingStatus;
  /** A camp paid in full from an emailed link: never "pay on arrival". */
  payByLink?: boolean;
  /** A restaurant table: held, not sold, so no amount is shown. */
  tableBooking?: boolean;
  /**
   * A women-only trip: the deposit sent to the organiser, the rest on the
   * day (dirhams), and whether the team has seen the deposit arrive. "My
   * bookings" draws the three steps from it.
   */
  womenTrip?: {
    depositAmount: number;
    restOnTheDay: number;
    depositReceived: boolean;
    /** The city she leaves from, when the trip has several. */
    departure?: string | null;
  };
  /**
   * The priced lines behind `total`, in dirhams, so the account card can show
   * the same sum of rounded lines the thank-you page shows (shownBookedTotal).
   * Missing on older entries: those convert `total` in one go.
   */
  breakdown?: {
    transportPrice?: number;
    discount?: number;
    packs?: Array<{ unitPrice: number; quantity: number; lineTotal: number }>;
    addons?: Array<{ quantity: number; lineTotal: number }>;
  };
  savedAt: number;
}

function isLocalBooking(x: unknown): x is LocalBooking {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === "string" &&
    typeof o.reference === "string" &&
    typeof o.slug === "string" &&
    typeof o.title === "string" &&
    typeof o.city === "string" &&
    typeof o.date === "string" &&
    typeof o.guests === "number" &&
    typeof o.total === "number" &&
    typeof o.currency === "string" &&
    typeof o.status === "string" &&
    typeof o.savedAt === "number"
  );
}

export function readLocalBookings(): LocalBooking[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(LOCAL_BOOKINGS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isLocalBooking).sort((a, b) => b.savedAt - a.savedAt);
  } catch {
    return [];
  }
}

function writeLocalBookings(items: LocalBooking[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCAL_BOOKINGS_STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent(LOCAL_BOOKINGS_CHANGED_EVENT));
  } catch {
    // Ignore private-mode quota and serialization failures.
  }
}

export function addLocalBooking(item: Omit<LocalBooking, "savedAt">): void {
  const existing = readLocalBookings();
  const prev = existing.find((b) => b.id === item.id);
  // Merged over the stored entry, not swapped for it: one writer may know a
  // flag (tableBooking, payByLink) another leaves out, and leaving it out
  // must not erase it.
  const defined = Object.fromEntries(
    Object.entries(item).filter(([, v]) => v !== undefined),
  ) as Omit<LocalBooking, "savedAt">;
  const next = [
    { ...prev, ...defined, savedAt: prev?.savedAt ?? Date.now() },
    ...existing.filter((b) => b.id !== item.id),
  ].slice(0, MAX_LOCAL_BOOKINGS);
  writeLocalBookings(next);
}

export function removeLocalBooking(id: string): void {
  writeLocalBookings(readLocalBookings().filter((b) => b.id !== id));
}

export function useLocalBookings(): { items: LocalBooking[]; hydrated: boolean } {
  const [items, setItems] = useState<LocalBooking[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const sync = () => setItems(readLocalBookings());
    sync();
    setHydrated(true);
    const onStorage = (e: StorageEvent) => {
      if (e.key === LOCAL_BOOKINGS_STORAGE_KEY) sync();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(LOCAL_BOOKINGS_CHANGED_EVENT, sync as EventListener);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(LOCAL_BOOKINGS_CHANGED_EVENT, sync as EventListener);
    };
  }, []);

  return { items, hydrated };
}
